import React from 'react';

/**
 * Shared building blocks for the signed-out auth screens (sign in / sign up).
 *
 * These screens follow the marketing site's Netlise Air language rather than
 * the dashboard's own token system: they are the bridge a visitor crosses from
 * netlise.com into the product, so they should look like where they came from.
 *
 * Colours here are literal, not `var(--…)` tokens, and that is deliberate. The
 * dashboard's `[data-theme="dark"]` attribute is set by the authenticated Admin
 * layout and can linger on <html> after a sign-out; a screen built half from
 * tokens and half from fixed hexes would render as a broken mix. Being wholly
 * literal, these always render as designed.
 *
 * Field and focus styling lives in index.css (.nl-field / .nl-pw-toggle) rather
 * than in onFocus/onBlur handlers, because those would have to mutate
 * element.style — which React owns on these inputs and rewrites on the next
 * render, so the focus ring would vanish whenever Formik updated state.
 */

export const INK = '#0F0F1A';
export const BODY = '#374151';
export const MUTED = '#6B7280';
export const BRAND = '#6366F1';
export const BRAND_DARK = '#4F46E5';
export const BORDER = '#E5E7EB';

export const fieldClass = (invalid?: boolean, pw?: boolean) =>
  ['nl-field', invalid ? 'nl-field--error' : '', pw ? 'nl-field--pw' : '']
    .filter(Boolean)
    .join(' ');

export const Label = ({children}: {children: React.ReactNode}) => (
  <span
    style={{display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: BODY}}
  >
    {children}
  </span>
);

export const FieldError = ({children}: {children?: string}) =>
  children ? (
    <span style={{display: 'block', marginTop: 6, fontSize: 13, color: '#EF4444'}}>
      {children}
    </span>
  ) : null;

/** Text field, Air spec: 48px tall, 14px radius, tinted until focused. */
export const AirField = ({
  id,
  label,
  value,
  error,
  touched,
  onChange,
  placeholder,
  type = 'text',
}: {
  id: string;
  label: string;
  value: string;
  error?: string;
  touched?: boolean;
  onChange: (v: any) => void;
  placeholder?: string;
  type?: string;
}) => {
  const invalid = !!error && !!touched;
  return (
    <label htmlFor={id} style={{display: 'block'}}>
      <Label>{label}</Label>
      <input
        id={id}
        name={id}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        className={fieldClass(invalid)}
      />
      <FieldError>{invalid ? error : undefined}</FieldError>
    </label>
  );
};

/** Password field with its own Show / Hide toggle. */
export const AirPassword = ({
  id,
  label,
  value,
  error,
  touched,
  onChange,
  shown,
  onToggle,
}: {
  id: string;
  label: string;
  value: string;
  error?: string;
  touched?: boolean;
  onChange: (v: any) => void;
  shown: boolean;
  onToggle: () => void;
}) => {
  const invalid = !!error && !!touched;
  return (
    <label htmlFor={id} style={{display: 'block'}}>
      <Label>{label}</Label>
      <div style={{position: 'relative'}}>
        <input
          id={id}
          name={id}
          type={shown ? 'text' : 'password'}
          value={value}
          placeholder="••••••••••"
          onChange={onChange}
          className={fieldClass(invalid, true)}
        />
        <button
          type="button"
          onClick={onToggle}
          aria-label={shown ? 'Hide password' : 'Show password'}
          className="nl-pw-toggle"
        >
          {shown ? 'Hide' : 'Show'}
        </button>
      </div>
      <FieldError>{invalid ? error : undefined}</FieldError>
    </label>
  );
};

export const PrimaryButton = ({
  children,
  busy,
  type = 'submit',
  onClick,
}: {
  children: React.ReactNode;
  busy?: boolean;
  type?: 'submit' | 'button';
  onClick?: () => void;
}) => (
  <button
    type={type}
    onClick={onClick}
    disabled={busy}
    style={{
      height: 52,
      width: '100%',
      borderRadius: 999,
      border: 'none',
      background: BRAND,
      color: '#fff',
      fontSize: 16,
      fontWeight: 600,
      cursor: busy ? 'default' : 'pointer',
      opacity: busy ? 0.75 : 1,
      boxShadow: '0 20px 40px -20px rgba(99,102,241,.95)',
      transition: 'background-color .2s ease',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.background = BRAND_DARK)}
    onMouseLeave={(e) => (e.currentTarget.style.background = BRAND)}
  >
    {children}
  </button>
);

export const GoogleButton = ({
  children,
  busy,
  onClick,
}: {
  children: React.ReactNode;
  busy?: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={busy}
    style={{
      height: 52,
      width: '100%',
      borderRadius: 999,
      border: `1px solid ${BORDER}`,
      background: '#fff',
      color: INK,
      fontSize: 16,
      fontWeight: 600,
      cursor: busy ? 'default' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      transition: 'border-color .2s ease',
    }}
    onMouseEnter={(e) => (e.currentTarget.style.borderColor = INK)}
    onMouseLeave={(e) => (e.currentTarget.style.borderColor = BORDER)}
  >
    <img src="/images/google-logo.png" alt="" width={18} height={18} />
    {children}
  </button>
);

/**
 * Page shell: washed background, decorative glow + grid, the real lockup, and
 * a split card whose left half is photography and right half the form.
 *
 * The card grid is `auto-fit minmax(min(100%,400px),1fr)`, so it becomes one
 * column on its own (image on top) without a breakpoint.
 */
export const AuthLayout = ({
  logo,
  image,
  imageAlt,
  imagePosition = 'center',
  chipTitle,
  children,
}: {
  logo: string;
  image: string;
  imageAlt: string;
  /** object-position for the panel photo. The panel is taller than the square
   *  source images, so `cover` crops ~260px of width — this chooses which side
   *  survives. Both current photos have a branded chip baked into them, and at
   *  the default `center` the sign-up one lands half-cut on the crop edge, so
   *  it is pushed right to show that chip whole. */
  imagePosition?: string;
  /** Optional glass chip over the photo. Omitted where the photograph already
   *  carries its own branded chip, which both current images do; rendering
   *  ours on top of theirs reads as a duplicated element. */
  chipTitle?: string;
  children: React.ReactNode;
}) => (
  <section
    className="nl-auth"
    style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '48px 24px 72px',
      background: 'linear-gradient(180deg,#F5F5FF 0%,#FFFFFF 85%)',
      overflow: 'hidden',
      position: 'relative',
      color: INK,
      WebkitFontSmoothing: 'antialiased',
    }}
  >
    <div
      aria-hidden
      style={{
        position: 'absolute',
        pointerEvents: 'none',
        top: -280,
        right: -120,
        width: 900,
        height: 700,
        background: 'radial-gradient(closest-side,rgba(99,102,241,.20),transparent 72%)',
        filter: 'blur(30px)',
      }}
    />
    <div
      aria-hidden
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        backgroundImage:
          'linear-gradient(rgba(15,15,26,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(15,15,26,.04) 1px,transparent 1px)',
        backgroundSize: '96px 96px',
        WebkitMaskImage: 'radial-gradient(110% 80% at 30% 0%,#000,transparent 70%)',
        maskImage: 'radial-gradient(110% 80% at 30% 0%,#000,transparent 70%)',
      }}
    />

    <a
      href="https://netlise.com"
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        textDecoration: 'none',
      }}
    >
      <img src={logo} alt="" width={36} height={36} style={{borderRadius: 11}} />
      <span style={{fontSize: 22, fontWeight: 600, letterSpacing: '-0.03em', color: INK}}>
        Netlise
      </span>
    </a>

    <div
      style={{
        position: 'relative',
        marginTop: 40,
        width: '100%',
        maxWidth: 1080,
        background: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: 32,
        padding: 12,
        boxShadow: '0 50px 90px -60px rgba(15,15,26,.35)',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,400px),1fr))',
        gap: 12,
      }}
    >
      <div
        style={{
          position: 'relative',
          borderRadius: 24,
          overflow: 'hidden',
          minHeight: 320,
          background: '#EEF2FF',
        }}
      >
        <img
          src={image}
          alt={imageAlt}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition: imagePosition,
            display: 'block',
          }}
        />
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg,rgba(15,15,26,0) 45%,rgba(15,15,26,.55) 100%)',
          }}
        />
        {chipTitle && (
        <div
          style={{
            position: 'absolute',
            left: 20,
            right: 20,
            bottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            background: 'rgba(255,255,255,.86)',
            backdropFilter: 'blur(16px) saturate(1.4)',
            WebkitBackdropFilter: 'blur(16px) saturate(1.4)',
            border: '1px solid rgba(255,255,255,.7)',
            borderRadius: 18,
            padding: '14px 16px',
          }}
        >
          <span
            aria-hidden
            style={{
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: 999,
              background: BRAND,
              color: '#fff',
              fontSize: 20,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
            }}
          >
            +
          </span>
          <div style={{minWidth: 0}}>
            <div style={{fontSize: 16, fontWeight: 600, letterSpacing: '-0.02em'}}>
              {chipTitle}
            </div>
            <div
              style={{
                fontFamily: "'Geist Mono', ui-monospace, monospace",
                fontSize: 12,
                color: MUTED,
              }}
            >
              dashboard.netlise.com
            </div>
          </div>
        </div>
        )}
      </div>

      <div
        style={{
          padding: 'clamp(24px,4vw,44px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {children}
      </div>
    </div>
  </section>
);

export const AuthHeading = ({children}: {children: React.ReactNode}) => (
  <h1
    style={{
      margin: 0,
      fontSize: 'clamp(30px,3vw,38px)',
      fontWeight: 500,
      lineHeight: 1.05,
      letterSpacing: '-0.035em',
    }}
  >
    {children}
  </h1>
);

/** "NEW · Create your store" style eyebrow. */
export const AuthEyebrow = ({tag, label}: {tag: string; label: string}) => (
  <div
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 10,
      border: '1px solid #E0E3FF',
      background: '#F5F5FF',
      borderRadius: 999,
      padding: '6px 14px 6px 7px',
    }}
  >
    <span
      style={{
        fontFamily: "'Geist Mono', ui-monospace, monospace",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        color: '#fff',
        background: BRAND,
        borderRadius: 999,
        padding: '3px 8px',
      }}
    >
      {tag}
    </span>
    <span style={{fontSize: 13, fontWeight: 500, color: BODY}}>{label}</span>
  </div>
);

export const AuthFooterNote = ({children}: {children: React.ReactNode}) => (
  <p style={{fontSize: 14, color: MUTED, textAlign: 'center'}}>{children}</p>
);
